import { Button } from '@bosch/react-frok'
import { forwardRef, useContext, useEffect, useImperativeHandle, useRef, useState } from 'react'
import type { Dispatch, SetStateAction } from 'react'
import { t } from 'i18next'
import { useVoiceVisualizer, VoiceVisualizer } from 'react-voice-visualizer'

// utils
import { useModal } from '@/contexts/modalContext'
import useWindowSize from '@/hooks/useWindowSize'
import { useAuthentication } from '@/hooks/useAuthentication'
import { ShowModalConfig } from '@/shared/types/modal'
import type { RehydrateData } from '@/shared/types/authentication'
import { ConfigContext } from '@/contexts/configContext'

import './InputAudio.scss'

export type InputAudioHandle = {
    startRecording: () => void
    stopRecording: (stream?: MediaStream) => void
    initMedia: () => Promise<void>
    setIsRecording: (isRecording: boolean) => void,
}

type Props = {
    assistant?: {
        activeChat?: {
                chatId?: string
        }
        sendAudio?: (audio: Blob, token: string) => Promise<unknown>
    }
    setQuestion: Dispatch<SetStateAction<string>>
    validateTokenAndRehydrate: ((dataToPersist?: RehydrateData) => Promise<boolean>) | undefined
  onRecordingChange: (isRecording: boolean) => void
  onTranscribingChange: (isTranscribing: boolean) => void
    persistedData?: {
        audio?: Blob
    }
    background?: 'colored' | 'transparent'
}

const isTranscriptionResult = (value: unknown): value is { transcript: string } =>
        typeof value === 'object'
        && value !== null
        && 'transcript' in value
        && typeof value.transcript === 'string'

const InputAudio = forwardRef<InputAudioHandle, Props>(({ assistant, setQuestion, validateTokenAndRehydrate, onRecordingChange, onTranscribingChange, persistedData, background = 'colored' }, ref) => {
    const { isMobile } = useWindowSize()
    const { isDarkTheme } = useContext(ConfigContext)
    const [isRecording, setIsRecording] = useState<boolean>(false)
    const [isTranscribing, setIsTranscribing] = useState<boolean>(false)
    const [transcriptionQueue, setTranscriptionQueue] = useState<Blob[]>([])
    const { getApiToken } = useAuthentication()
    const cancelNextRef = useRef(false)
    const interruptionHandledRef = useRef(false)
    const recordingStartTimeRef = useRef(0)
    const audioFailureTimeoutRef = useRef<number | null>(null)

    const { showModal, hideModal } = useModal()

    const clearAudioFailureTimeout = () => {
        if (audioFailureTimeoutRef.current) {
            window.clearTimeout(audioFailureTimeoutRef.current)
            audioFailureTimeoutRef.current = null
        }
    }

    const finishRecording = (keepCancelFlag = false) => {
        clearAudioFailureTimeout()
        if (!keepCancelFlag) {
            cancelNextRef.current = false
        }
        setIsRecording(false)
        onRecordingChange(false)
        if (keepCancelFlag) {
            clearCanvas()
        }
    }

    // Responsive settings for voice visualizer
    const visualizerSettings = {
        barWidth: isMobile ? 6 : 3,
        gap: isMobile ? 1.5 : 1,
        height: isMobile ? 64 : 48,
        speed: isMobile ? 0.4 : 0.3,
    }

    const onStartRecording = async () => {
        clearAudioFailureTimeout()
        cancelNextRef.current = false
        interruptionHandledRef.current = false
        recordingStartTimeRef.current = Date.now()
        setIsRecording(true)
        onRecordingChange(true)
    }

    const onStopRecording = () => {
        finishRecording(cancelNextRef.current)
    }

    const recorderControls = useVoiceVisualizer({
        onStartRecording,
        onStopRecording,
    })

    const {
        recordedBlob,
        error,
        isPausedRecording,
        isRecordingInProgress,
        mediaRecorder,
        formattedRecordingTime,
        startRecording,
        stopRecording,
        togglePauseResume,
        clearCanvas,
    } = recorderControls;

    useEffect(() => {
        if (!recordedBlob || error) return
        // skip transcription when canceled
        if (cancelNextRef.current) {
            cancelNextRef.current = false
            return
        }
        actionsRef.current.transcribeAudio(recordedBlob)
    }, [recordedBlob, error])

    useEffect(() => {
        onTranscribingChange?.(isTranscribing)
    }, [isTranscribing, onTranscribingChange])

    // The parent can unmount this component while it transcribes (the input area hides it as soon as there is text),
    // and then it would never learn that the transcription ended
    const onTranscribingChangeRef = useRef(onTranscribingChange)
    onTranscribingChangeRef.current = onTranscribingChange
    useEffect(() => () => onTranscribingChangeRef.current?.(false), [])

    useEffect(() => {
        if (!error || !isRecording || interruptionHandledRef.current) {
            return
        }

        interruptionHandledRef.current = true
        console.error('Recording error:', error)
        actionsRef.current.onShowMuteModal()
        actionsRef.current.cancelRecording()
    }, [error, isRecording])

    const initMedia = async () => {
        try {
            clearAudioFailureTimeout()
            
            // Quick permission check - throws immediately if mic is blocked (no browser delay)
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
            stream.getTracks().forEach(track => track.stop())
            
            // Permission granted, proceed with actual recording
            await startRecording()
            recordingStartTimeRef.current = Date.now()
            setIsRecording(true)
            onRecordingChange(true)
        } catch (error: unknown) {
            const message = error instanceof Error ? error.message : String(error)
            console.error(`Error starting audio recording: ${message}`)
            onShowMuteModal()
        }
    }

    useEffect(() => {
        if (!isRecording) return

        clearAudioFailureTimeout()

        const hasActiveRecording = isRecordingInProgress || Boolean(recordedBlob) || Boolean(error)
        if (hasActiveRecording || isTranscribing) {
            return
        }

        audioFailureTimeoutRef.current = window.setTimeout(() => {
            const recordingDuration = (Date.now() - recordingStartTimeRef.current) / 1000
            if (recordingDuration > 3 && !isTranscribing) {
                console.warn('[InputAudio] No audio blob produced within timeout.')
                actionsRef.current.onShowMuteModal()
                actionsRef.current.cancelRecording()
            }
        }, 4000)

        return () => {
            clearAudioFailureTimeout()
        }
    }, [isRecording, isRecordingInProgress, recordedBlob, error, isTranscribing])

    useEffect(() => {
        if (!isRecording || !mediaRecorder?.stream) {
            return
        }

        const tracks = mediaRecorder.stream.getAudioTracks()
        if (!tracks.length) {
            return
        }

        const handleInterruptedTrack = () => {
            if (interruptionHandledRef.current) {
                return
            }

            interruptionHandledRef.current = true
            console.warn('[InputAudio] Microphone interrupted during recording.')
            actionsRef.current.onShowMuteModal()
            actionsRef.current.cancelRecording()
        }

        tracks.forEach((track) => {
            track.addEventListener('ended', handleInterruptedTrack)
            track.addEventListener('mute', handleInterruptedTrack)
        })

        return () => {
            tracks.forEach((track) => {
                track.removeEventListener('ended', handleInterruptedTrack)
                track.removeEventListener('mute', handleInterruptedTrack)
            })
        }
    }, [isRecording, mediaRecorder])

    useEffect(() => {
        if (!isTranscribing && transcriptionQueue.length > 0) {
            const nextTranscriptionBlob = transcriptionQueue[0]
            setTranscriptionQueue((prev) => prev.slice(1))
            actionsRef.current.transcribeAudio(nextTranscriptionBlob)
        }
    }, [isTranscribing, transcriptionQueue])

    useEffect(() => {
        if (persistedData && persistedData.audio)
            actionsRef.current.transcribeAudio(persistedData.audio)

    }, [persistedData])

    const onShowMuteModal = () => {
        const data: ShowModalConfig = {
            title: t('chat:audio:mutedTitle'),
            content: t('chat:audio:mutedText'),
            confirmLabel: t('general:buttons:confirm'),
            onConfirmClick: async () => { hideModal() },
            variant: 'warning',
        }
        showModal(data)
    }

    const transcribeAudio = async (audioBlob: Blob) => {
        const recordingStartTime = recordingStartTimeRef.current
        try {
            setIsTranscribing(true)
            const recordingDuration = recordingStartTime ? (Date.now() - recordingStartTime) / 1000 : 0

            if (recordingDuration < 0.2 && audioBlob.size < 5000 && recordingStartTime) {
                console.warn(t('chat:audio:recordingTooShort'))
                return
            }

            const fileExtension = audioBlob.type.split('/')[1] || 'webm'
            const file = new File([audioBlob], `audiochunk.${fileExtension}`, {
                type: audioBlob.type,
            })

            if (file.size > 0) {
                const token = await getApiToken()
                let isTokenValid = true
                if (validateTokenAndRehydrate) {
                    isTokenValid = await validateTokenAndRehydrate({audio: file, chatId: assistant?.activeChat?.chatId})
                }

                if (!isTokenValid) {
                    console.warn('Token validation failed for audio transcription')
                    return
                }

                if (!assistant?.sendAudio) {
                    return
                }

                const result = await assistant.sendAudio(file, token)

                if (isTranscriptionResult(result) && result.transcript) {
                    // cause transcription is halluzinating (https://github.com/openai/whisper/discussions/928)
                    // todo: find better process: detect silent audio, prevent halluzination, cover more halluzination phrases
                    if (result.transcript.includes('Untertitel der Amara.org-Community')) {
                        console.error("recording failed - hallucination detected")
                    } else if (result.transcript.trim()) {
                        setQuestion((prev: string) => (prev + ' ' + result.transcript).trim())
                    }
                }
            }
        } catch (error) {
            console.error('Transcription error:', error)
        } finally {
            setIsTranscribing(false)
            recordingStartTimeRef.current = 0
        }
    }

    const cancelRecording = () => {
        // mark cancel so no text is appended and stop the recorder
        cancelNextRef.current = true
        interruptionHandledRef.current = true
        stopRecording()
        finishRecording(true)
    }

    // The effects above read these through a ref: they are new functions in every render, and an effect must not run
    // again (and transcribe or cancel again) because of that
    const actionsRef = useRef({ transcribeAudio, cancelRecording, onShowMuteModal, initMedia })
    actionsRef.current = { transcribeAudio, cancelRecording, onShowMuteModal, initMedia }

    useImperativeHandle(ref, () => ({
        startRecording: startRecording,
        stopRecording: stopRecording,
        initMedia: () => actionsRef.current.initMedia(),
        setIsRecording,
    }), [startRecording, stopRecording]);

    return (
        <>
            {isRecording &&
                <div className={`audio-input-container${background === 'transparent' ? ' -transparent' : ''}`}>
                    <div className='audio-controls'>
                        <Button
                            className='audio-cancel-button '
                            mode="integrated"
                            icon={{
                                iconName: 'delete',
                                title: t('chat:audio:cancel'),
                            }}
                            onClick={cancelRecording}
                        />
                        <Button 
                            className='audio-button' 
                            mode="integrated"
                            icon={{ iconName: isPausedRecording ? 'recording' : 'pause', title: isPausedRecording ? t('chat:audio:resume') : t('chat:audio:pause') }}
                            onClick={togglePauseResume}
                        />
                    </div>

                    <VoiceVisualizer
                        controls={recorderControls}
                        width="100%"
                        height={visualizerSettings.height}
                        speed={visualizerSettings.speed}
                        barWidth={visualizerSettings.barWidth}
                        gap={visualizerSettings.gap}
                        rounded={0}
                        fullscreen={true}
                        onlyRecording={isRecording}
                        isControlPanelShown={false}
                        backgroundColor='transparent'
                        mainBarColor={isDarkTheme ? '#FFFFFF' : '#000000'}
                        secondaryBarColor='#5e5e5e'
                        isDefaultUIShown={false}
                        isProgressIndicatorShown={false}
                        isProgressIndicatorTimeOnHoverShown={false}
                        isProgressIndicatorTimeShown={false}
                        isProgressIndicatorOnHoverShown={false}
                    />
                    <div className='audio-time'>{formattedRecordingTime}</div>
                </div>
            }
        </>
    )
})

InputAudio.displayName = 'InputAudio'

export default InputAudio
